import React from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import {
  useAccessibilityPrefsStore,
  FONT_SCALE_OPTIONS,
  FONT_FAMILY_OPTIONS,
} from '@/stores/useAccessibilityPrefsStore';

const DRAWER_WIDTH = 340;

interface AccessibilityDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const AccessibilityDrawer: React.FC<AccessibilityDrawerProps> = ({ open, onClose }) => {
  const {
    fontScale,
    fontFamily,
    lineHeightLoose,
    wideLetterSpacing,
    underlineLinks,
    highlightFocus,
    reduceMotion,
    setFontScale,
    setFontFamily,
    setLineHeightLoose,
    setWideLetterSpacing,
    setUnderlineLinks,
    setHighlightFocus,
    setReduceMotion,
    resetAll,
  } = useAccessibilityPrefsStore();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {/* Header */}
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          px: 2,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" component="h2">
          Accessibility
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close accessibility panel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Toolbar>

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {/* ---- Font Size ---- */}
        <Section label="Font Size">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
              {FONT_SCALE_OPTIONS.find((o) => o.value === fontScale)?.label ?? `${Math.round(fontScale * 100)}%`}
            </Typography>
            <Slider
              value={fontScale}
              onChange={(_, v) => setFontScale(v as number)}
              min={0.85}
              max={1.25}
              step={null}
              marks={FONT_SCALE_OPTIONS.map((o) => ({ value: o.value }))}
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              valueLabelDisplay="auto"
              aria-label="Font size"
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">85%</Typography>
            <Typography variant="caption" color="text.secondary">125%</Typography>
          </Stack>
        </Section>

        <Divider sx={{ my: 2 }} />

        {/* ---- Font Family ---- */}
        <Section label="Font Family">
          <Select
            fullWidth
            size="small"
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value as typeof fontFamily)}
            aria-label="Font family"
          >
            {FONT_FAMILY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: opt.css }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Section>

        <Divider sx={{ my: 2 }} />

        {/* ---- Reading ---- */}
        <Section label="Reading">
          <ToggleRow
            label="Increased line spacing"
            checked={lineHeightLoose}
            onChange={setLineHeightLoose}
          />
          <ToggleRow
            label="Wide letter spacing"
            checked={wideLetterSpacing}
            onChange={setWideLetterSpacing}
          />
        </Section>

        <Divider sx={{ my: 2 }} />

        {/* ---- Visual Aids ---- */}
        <Section label="Visual Aids">
          <ToggleRow
            label="Underline all links"
            checked={underlineLinks}
            onChange={setUnderlineLinks}
          />
          <ToggleRow
            label="Highlight focused elements"
            checked={highlightFocus}
            onChange={setHighlightFocus}
          />
          <ToggleRow
            label="Reduce motion"
            checked={reduceMotion}
            onChange={setReduceMotion}
          />
        </Section>

        <Divider sx={{ my: 2 }} />

        {/* ---- Reset ---- */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<ResetIcon />}
          onClick={resetAll}
          aria-label="Reset accessibility settings to defaults"
        >
          Reset to Defaults
        </Button>
      </Box>
    </Drawer>
  );
};

/* ---- helper components ---- */

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <FormControlLabel
    control={<Switch checked={checked} onChange={(_, v) => onChange(v)} />}
    label={label}
    sx={{ display: 'flex', ml: 0, mb: 0.5 }}
  />
);
